"use client"

import type posthog from "posthog-js"
import type { PostHog } from "posthog-js"
import { useEffect, useState } from "react"
import { Ccm19EmbeddingName } from "src/integrations/ccm19/embeddings"
import { useCcm19EmbeddingAccepted } from "src/integrations/ccm19/useConsented"
import { integrationDebug } from "src/integrations/lib/api"
import { posthogPublicWriteKey } from "src/integrations/posthog/constants"
import { posthogDebug } from "src/integrations/posthog/debug"
import { posthogRewriteBasePath } from "src/integrations/posthog/rewrites"
import { updateFeatureFlags } from "src/integrations/posthog/useExperiment"
import { getUserId } from "src/tracking/state"
import { isBrowser } from "src/utilities/env"
import { runOnceInBrowser } from "src/utilities/runOnceInBrowser"

const options: Parameters<typeof posthog.init>[1] = {
  // https://posthog.com/docs/libraries/js#config
  api_host: posthogRewriteBasePath,
  ui_host: "https://eu.posthog.com",
  defaults: "2025-05-24",
  person_profiles: "identified_only",
  opt_out_capturing_by_default: true,
  opt_out_persistence_by_default: true,
  capture_pageview: false, // We handle page views manually.
  capture_pageleave: true, // Must be enabled manually if capture_pageview is false.
  disable_surveys: true,

  bootstrap: {
    distinctID: getUserId(),
    isIdentifiedID: true,
  },

  // https://posthog.com/docs/libraries/js#redacting-information-in-events
  before_send: (event) => {
    if (!event) {
      return null
    }

    if (event.event === "$exception") {
      const exceptionList = event.properties.$exception_list || []

      for (const exception of exceptionList) {
        const message = exception.value

        if (/Loading chunk \d+ failed/.test(message) || message.includes("ipwho.is")) {
          return null
        }
      }
    }

    return event
  },
}

let posthogWeb: PostHog | null = null

export function usePosthogWeb() {
  const consented = useCcm19EmbeddingAccepted(Ccm19EmbeddingName.Posthog)
  const [posthog, setPosthog] = useState<PostHog | null>(null)

  useEffect(() => {
    if (consented) {
      runOnceInBrowser("posthog.init", () => {
        import("posthog-js").then(({ posthog }) => {
          posthogDebug("init", options)
          posthog.init(posthogPublicWriteKey, options)
          posthog.featureFlags.onFeatureFlags(updateFeatureFlags)
          setPosthog(posthog)
          posthogWeb = posthog
          integrationDebug("posthog", "initialized")
        })
      })
    }
  }, [consented])

  return posthog
}

/** Calls the provided function as soon as PostHog feature flags are available. */
export function withPosthogWeb(callback: (posthog: PostHog) => void) {
  if (!isBrowser) {
    return
  }

  if (posthogWeb) {
    callback(posthogWeb)
  } else {
    const interval = setInterval(() => {
      if (posthogWeb) {
        clearInterval(interval)
        callback(posthogWeb)
      }
    }, 100)
  }
}
